import { UserRole } from '@features/settings/types/settings.types';
import { useNotifications } from '@shared/hooks/useNotifications';
import { useProfile } from '@shared/hooks/useProfile';
import {
  Timestamp,
  addDoc,
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  setDoc,
  startAfter,
  updateDoc,
  writeBatch,
} from 'firebase/firestore';
import { useCallback, useEffect, useRef, useState } from 'react';
import { db } from '../../../config/firebase';
import {
  SUPPORT_MESSAGES_COLLECTION,
  SUPPORT_TICKETS_COLLECTION,
  SUPPORT_TYPING_STATUS_COLLECTION,
} from '../constants/collections';
import { SupportMessage } from '../types';

/**
 * Hook to manage support chat messages
 */
export const useSupportMessages = (ticketId: string | null) => {
  const { user } = useProfile();
  const [messages, setMessages] = useState<SupportMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const [typingUsers, setTypingUsers] = useState<Record<string, boolean>>({});
  const lastMessageRef = useRef<any>(null);
  const isLoadingMoreRef = useRef(false);
  const messagesPerPage = 20;
  const { createNotification } = useNotifications();


  // Use centralized collection names

  // Subscribe to messages for the selected ticket
  useEffect(() => {
    setMessages([]);
    setLoading(true);
    setError(null);
    setHasMore(true);
    lastMessageRef.current = null;
    isLoadingMoreRef.current = false;

    if (!ticketId || !user) {
      setLoading(false);
      return;
    }

    const isAdmin = user.role === UserRole.ADMIN;

    // Query for most recent messages
    const q = query(
      collection(
        db,
        SUPPORT_TICKETS_COLLECTION,
        ticketId,
        SUPPORT_MESSAGES_COLLECTION
      ),
      orderBy('timestamp', 'desc'),
      limit(messagesPerPage)
    );

    // Real-time subscription to messages
    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        let shouldPlaySound = false;
        const changes = snapshot.docChanges();

        // Process only added or modified messages
        changes.forEach((change) => {
          if (change.type === 'added' || change.type === 'modified') {
            const newMessage = {
              id: change.doc.id,
              ...change.doc.data(),
              timestamp: change.doc.data().timestamp?.toDate(),
            } as SupportMessage;

            // Determine if the notification sound should be played
            if (change.type === 'added' && newMessage.senderId !== user.id) {
              shouldPlaySound = true;
            }

            setMessages((prev) => {
              // Check if message already exists
              const messageIndex = prev.findIndex(
                (msg) => msg.id === newMessage.id
              );

              if (messageIndex !== -1) {
                // Update existing message
                const updatedMessages = [...prev];
                updatedMessages[messageIndex] = newMessage;
                return updatedMessages;
              } else {
                // Add new message
                return [...prev, newMessage].sort(
                  (a, b) => a.timestamp.getTime() - b.timestamp.getTime()
                );
              }
            });

            // Mark message as read if it's not from current user
            if (!newMessage.read && newMessage.senderId !== user.id) {
              updateDoc(
                doc(
                  db,
                  SUPPORT_TICKETS_COLLECTION,
                  ticketId,
                  SUPPORT_MESSAGES_COLLECTION,
                  newMessage.id
                ),
                { read: true }
              );

              // Update ticket unread count
              const ticketRef = doc(db, SUPPORT_TICKETS_COLLECTION, ticketId);
              getDoc(ticketRef).then((ticketDoc) => {
                if (ticketDoc.exists()) {
                  const ticketData = ticketDoc.data();
                  updateDoc(ticketRef, {
                    // If admin, reduce unreadAdminCount, otherwise reduce unreadUserCount
                    unreadAdminCount: isAdmin
                      ? 0
                      : ticketData.unreadAdminCount || 0,
                    unreadUserCount: !isAdmin
                      ? 0
                      : ticketData.unreadUserCount || 0,
                  });
                }
              });
            }
          }
        });

        // Play notification sound if needed
        /* if (shouldPlaySound) {
          playNotificationSound();
        } */

        // Update pagination state
        if (snapshot.docs.length > 0) {
          lastMessageRef.current = snapshot.docs[snapshot.docs.length - 1];
          setHasMore(snapshot.docs.length === messagesPerPage);
        }

        setLoading(false);
      },
      (err) => {
        console.error('Error loading support messages:', err);
        setError(err.message);
        setLoading(false);
      }
    );

    // Subscribe to typing status
    const typingStatusQuery = query(
      collection(
        db,
        SUPPORT_TICKETS_COLLECTION,
        ticketId,
        SUPPORT_TYPING_STATUS_COLLECTION
      )
    );

    const typingUnsubscribe = onSnapshot(typingStatusQuery, (snapshot) => {
      const typingData: Record<string, boolean> = {};

      snapshot.docs.forEach((doc) => {
        const data = doc.data();
        // Check if typing status is recent (within last 10 seconds)
        const isRecent =
          data.timestamp &&
          new Date().getTime() - data.timestamp.toDate().getTime() < 10000;

        if (isRecent && data.userId !== user.id) {
          typingData[data.userId] = data.isTyping;
        }
      });

      setTypingUsers(typingData);
    });

    return () => {
      unsubscribe();
      typingUnsubscribe();
    };
  }, [ticketId, user]);

  /**
   * Update typing status
   */
  const updateTypingStatus = useCallback(
    async (isTyping: boolean) => {
      if (!ticketId || !user?.id) return;

      try {
        const typingRef = doc(
          db,
          SUPPORT_TICKETS_COLLECTION,
          ticketId,
          SUPPORT_TYPING_STATUS_COLLECTION,
          user.id
        );
        await setDoc(typingRef, {
          userId: user.id,
          isTyping,
          timestamp: Timestamp.now(),
        });
      } catch (error) {
        console.error('Error updating typing status:', error);
      }
    },
    [ticketId, user]
  );

  /**
   * Load older messages for pagination
   */
  const loadMoreMessages = async () => {
    if (
      !hasMore ||
      !lastMessageRef.current ||
      loading ||
      isLoadingMoreRef.current ||
      !ticketId
    ) {
      return false;
    }

    try {
      isLoadingMoreRef.current = true;

      const q = query(
        collection(
          db,
          SUPPORT_TICKETS_COLLECTION,
          ticketId,
          SUPPORT_MESSAGES_COLLECTION
        ),
        orderBy('timestamp', 'desc'),
        startAfter(lastMessageRef.current),
        limit(messagesPerPage)
      );

      const snapshot = await getDocs(q);

      if (snapshot.empty) {
        setHasMore(false);
        return false;
      }

      const olderMessages = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
        timestamp: doc.data().timestamp?.toDate(),
      })) as SupportMessage[];

      // Update last document reference
      lastMessageRef.current = snapshot.docs[snapshot.docs.length - 1];

      // Sort chronologically and merge with existing messages
      const sortedOlderMessages = olderMessages.sort(
        (a, b) => a.timestamp.getTime() - b.timestamp.getTime()
      );

      setMessages((prev) => {
        // Filter out any potential duplicates
        const existingIds = new Set(prev.map((msg) => msg.id));
        const uniqueNewMessages = sortedOlderMessages.filter(
          (msg) => !existingIds.has(msg.id)
        );

        if (uniqueNewMessages.length === 0) {
          setHasMore(false);
          return prev;
        }

        return [...uniqueNewMessages, ...prev];
      });

      setHasMore(snapshot.docs.length === messagesPerPage);
      return true;
    } catch (err: any) {
      console.error('Error loading more messages:', err);
      setError(err.message);
      return false;
    } finally {
      isLoadingMoreRef.current = false;
    }
  };

  /**
   * Send a message in the support chat
   */
  const sendMessage = async (
    content: string,
    type: SupportMessage['type'] = 'text',
    mediaUrl?: string
  ) => {
    if (!ticketId || !user?.id) return;

    try {
      const isAdmin = user.role === UserRole.ADMIN;
      const timestamp = Timestamp.now();

      // Get ticket to notify the other party
      const ticketRef = doc(db, SUPPORT_TICKETS_COLLECTION, ticketId);
      const ticketSnap = await getDoc(ticketRef);

      if (!ticketSnap.exists()) return;

      const ticketData = ticketSnap.data();
      // For admin messages, receiver is the ticket creator
      // For user messages, receivers are all admins
      const receiverId = isAdmin ? ticketData.userId : 'admin';

      // Create message
      const messageData = {
        content,
        senderId: user.id,
        senderRole: user.role,
        timestamp,
        read: false,
        type,
        ...(mediaUrl && { mediaUrl }),
      };

      const messageRef = await addDoc(
        collection(
          db,
          SUPPORT_TICKETS_COLLECTION,
          ticketId,
          SUPPORT_MESSAGES_COLLECTION
        ),
        messageData
      );

      // Update ticket with last message and timestamp
      const batch = writeBatch(db);

      batch.update(ticketRef, {
        lastMessage: {
          id: messageRef.id,
          ...messageData,
        },
        updatedAt: timestamp,
        // Increment unread counter for the recipient
        ...(isAdmin
          ? { unreadUserCount: (ticketData.unreadUserCount || 0) + 1 }
          : { unreadAdminCount: (ticketData.unreadAdminCount || 0) + 1 }),
      });

      await batch.commit();

      // Send notification
      // Disabled for now
      /* try {
        await createNotification({
          receiverId: receiverId,
          type: NotificationType.SUPPORT_MESSAGE,
          metadata: {
            actorId: user.id,
            ticketId,
            messageType: type,
            preview: type === 'text' ? content.substring(0, 100) : `[${type}]`,
          },
        });
      } catch (notificationError) {
        console.error(
          'Error to send notification, but the message was sent:',
          notificationError
        );
      } */
    } catch (error) {
      console.error('Error sending support message:', error);
      throw error;
    }
  };

  /**
   * Delete a message from the support chat
   */
  const deleteMessage = useCallback(
    async (messageId: string) => {
      if (!ticketId || !user) return;

      try {
        const messageRef = doc(
          db,
          SUPPORT_TICKETS_COLLECTION,
          ticketId,
          SUPPORT_MESSAGES_COLLECTION,
          messageId
        );
        const messageDoc = await getDoc(messageRef);

        if (!messageDoc.exists()) {
          throw new Error('Message not found');
        }

        const messageData = messageDoc.data();
        if (messageData.senderId !== user.id) {
          throw new Error('You can only delete your own messages');
        }

        // Update message type to deleted
        await updateDoc(messageRef, {
          type: 'deleted',
          content: '',
          mediaUrl: null,
        });

        // Update last message if needed
        const lastMessage = messages[messages.length - 1];
        if (lastMessage?.id === messageId) {
          await updateDoc(doc(db, SUPPORT_TICKETS_COLLECTION, ticketId), {
            lastMessage: {
              ...lastMessage,
              type: 'deleted',
              content: '',
              mediaUrl: null,
            },
          });
        }
      } catch (error: any) {
        console.error('Error deleting message:', error);
        throw error;
      }
    },
    [ticketId, user, messages]
  );

  return {
    messages,
    loading,
    error,
    sendMessage,
    deleteMessage,
    loadMoreMessages,
    hasMore,
    updateTypingStatus,
    typingUsers,
  };
};
